// Oike — cobros (Charges): the rules, pure (charges.mts and the tests use the same functions).
// Design: docs/servicios/cobros.md. A charge is what is asked of someone; it is never a fictitious
// quote. Its state (paid, partial, overdue, pending) is computed from the applications of confirmed
// payments, never stored. Nothing here writes.
import { CHARGE, PAYMENT, APPLICATION, CHARGE_ORIGINS, PAYER_TYPES, INSTALLMENT_KINDS } from './billingSchema.js';
import { classifyRecipient, hasQuote } from './personQuotes.js';
import { isAccepted } from './quoteRules.js';

const REC_RE = /^rec[A-Za-z0-9]{14}$/;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const KEY_RE = /^[A-Za-z0-9-]{8,64}$/;
const CURRENCY_RE = /^[A-Z]{3}$/;
const links = (v) => (Array.isArray(v) ? v.map(x => (typeof x === 'object' ? x?.id : x)).filter(Boolean) : []);
const sel = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v.name : v) || '';
const text = (v, max) => String(v ?? '').replace(/\s+/g, ' ').trim().slice(0, max);
const cents = (n) => Math.round(Number(n) * 100);
const money = (c) => c / 100;

export class ChargeInputError extends Error { constructor(code) { super(code); this.code = code; } }
const fail = (code) => { throw new ChargeInputError(code); };

/** More than one record in a link that must hold one (Airtable allows several; the server refuses). */
export function singleLinkProblem(fields = {}, singleLinks = []) {
  return singleLinks.find(k => Array.isArray(fields[k]) && fields[k].length > 1) ? 'single_link' : null;
}

/** The plan shapes Oike accepts: one full charge; advance + balance; two or more installments. */
function checkPlanShape(plan) {
  const kinds = plan.map(p => p.kind);
  if (kinds.length === 1 && ['full', 'advance'].includes(kinds[0])) return;
  if (kinds.length === 2 && kinds[0] === 'advance' && kinds[1] === 'balance') return;
  if (kinds.length >= 2 && kinds.every(k => k === 'installment')) return;
  fail('invalid_plan');
}

/**
 * The fields of the charge(s) to create (one per installment), after every check. Throws a code.
 * input: { origin, payerType, stakeholderId, accountId, accountIsReference, beneficiaryId, quoteId,
 *          concept, currency, plan: [{ kind, amount, dueOn }], key }
 * ctx: { quote (the version's record, for origin quote), payerName, by, now }
 */
export function newChargeFields(input = {}, ctx = {}) {
  const now = ctx.now || new Date();
  const origin = input.origin;
  if (!CHARGE_ORIGINS.includes(origin)) fail('invalid_origin');
  const payerType = input.payerType;
  if (!PAYER_TYPES.includes(payerType)) fail('payer_required');
  const stakeholderId = input.stakeholderId || '';
  const accountId = input.accountId || '';
  const beneficiaryId = input.beneficiaryId || '';
  for (const id of [stakeholderId, accountId, beneficiaryId, input.quoteId || '']) if (id && !REC_RE.test(id)) fail('invalid_link');
  const accountIsReference = !!input.accountIsReference;
  if (payerType === 'person') {
    if (!stakeholderId) fail('person_required');
    if (accountId && !accountIsReference) fail('company_only_reference'); // her company never pays her charge
    if (beneficiaryId && beneficiaryId !== stakeholderId) fail('beneficiary_only_company');
  } else {
    if (!accountId) fail('company_required');
    if (accountIsReference) fail('company_is_payer');
  }
  // From a quote: the exact version, already sent or accepted, and the same payer and currency
  let quoteId = '';
  if (origin === 'quote') {
    const q = ctx.quote;
    quoteId = input.quoteId || '';
    if (!quoteId || !q || q.id !== quoteId || !hasQuote(q.fields || {})) fail('quote_required');
    const f = q.fields || {};
    // Accepted by the client (Quote Status = Won, the evidence Oike records); an internal approval or a publication is not enough
    if (!isAccepted(f)) fail('quote_not_accepted');
    const recipient = classifyRecipient(f);
    if (recipient.kind === 'review') fail('quote_recipient_unknown');
    if (recipient.kind === 'personal' && (payerType !== 'person' || links(f['Stakeholder'])[0] !== stakeholderId)) fail('quote_payer_mismatch');
    if (recipient.kind === 'company' && (payerType !== 'company' || links(f['Account'])[0] !== accountId)) fail('quote_payer_mismatch');
    if ((f['Quote Currency'] || 'USD') !== String(input.currency || '')) fail('quote_currency_mismatch');
  } else if (input.quoteId) fail('direct_without_quote');

  const concept = text(input.concept, 200);
  if (concept.length < 2) fail('concept_required');
  const currency = String(input.currency || '');
  if (!CURRENCY_RE.test(currency)) fail('invalid_currency');
  const plan = Array.isArray(input.plan) ? input.plan : [];
  if (!plan.length || plan.length > 24) fail('invalid_plan');
  for (const p of plan) {
    if (!INSTALLMENT_KINDS.includes(p.kind)) fail('invalid_plan');
    const n = Number(p.amount);
    if (!Number.isFinite(n) || n <= 0 || n > 1e9 || Math.abs(n * 100 - Math.round(n * 100)) > 1e-6) fail('invalid_amount'); // at most 2 decimals
    if (!DAY_RE.test(String(p.dueOn || '')) || Number.isNaN(Date.parse(`${p.dueOn}T12:00:00Z`))) fail('invalid_due');
  }
  checkPlanShape(plan);
  if (!KEY_RE.test(String(input.key || ''))) fail('key_required');

  // An independent additional contract on a quote version: only explicit, with a reason
  let additional = '';
  if (input.additional) {
    if (origin !== 'quote') fail('additional_only_quote');
    additional = text(input.additional?.reason, 300);
    if (additional.length < 10) fail('additional_reason_required');
  }
  const many = plan.length > 1;
  const who = text(ctx.payerName, 120) || (payerType === 'person' ? 'Persona' : 'Empresa');
  const stamp = now.toISOString();
  return plan.map((p, i) => {
    const kindLabel = p.kind === 'advance' ? 'anticipo' : p.kind === 'balance' ? 'saldo' : p.kind === 'installment' ? `cuota ${i + 1}/${plan.length}` : '';
    const fields = {
      [CHARGE.name]: `${concept}${kindLabel ? ` (${kindLabel})` : ''} · ${who}`,
      [CHARGE.origin]: origin,
      [CHARGE.payerType]: payerType,
      [CHARGE.concept]: concept,
      [CHARGE.amount]: money(cents(p.amount)),
      [CHARGE.currency]: currency,
      [CHARGE.dueOn]: p.dueOn,
      [CHARGE.installmentKind]: p.kind,
      [CHARGE.status]: 'open',
      [CHARGE.requestedAt]: stamp,
      [CHARGE.createdBy]: text(ctx.by, 200),
      [CHARGE.key]: `${input.key}-${i + 1}`,
      // With several installments, the agreed plan travels in every charge, so a plan left incomplete can be completed
      [CHARGE.log]: `${additional ? `${ADDITIONAL_MARK} ` : ''}${stamp.slice(0, 16).replace('T', ' ')} UTC · ${text(ctx.by, 200)}: cobro registrado${additional ? ` como contratación adicional independiente. Motivo: ${additional}` : ''}.${many ? `\n${encodePlan(plan)}` : ''}`,
    };
    if (stakeholderId) fields[CHARGE.stakeholder] = [stakeholderId];
    if (accountId) { fields[CHARGE.account] = [accountId]; fields[CHARGE.accountIsReference] = accountIsReference; }
    if (beneficiaryId && payerType === 'company') fields[CHARGE.beneficiary] = [beneficiaryId];
    if (quoteId) fields[CHARGE.quote] = [quoteId];
    const method = text(input.plannedMethod, 80);
    if (method) fields[CHARGE.plannedMethod] = method;
    if (many) {
      fields[CHARGE.planKey] = `plan-${input.key}`;
      fields[CHARGE.installmentNumber] = i + 1;
      fields[CHARGE.installmentsTotal] = plan.length;
    }
    return fields;
  });
}

/**
 * The state of a charge, computed from the applications of confirmed movements (never stored):
 * collected = applied from confirmed payments − applied from confirmed refunds; pending = amount − collected.
 */
export function chargeState(charge, { applications = [], payments = [], today = new Date().toISOString().slice(0, 10) } = {}) {
  const f = charge?.fields || {};
  const amountC = cents(Number(f[CHARGE.amount]) || 0);
  const byId = new Map(payments.map(p => [p.id, p]));
  let collectedC = 0;
  for (const a of applications) {
    const af = a.fields || {};
    if (sel(af[APPLICATION.status]) !== 'active' || !links(af[APPLICATION.charge]).includes(charge.id)) continue;
    const p = byId.get(links(af[APPLICATION.payment])[0]);
    if (!p || sel(p.fields?.[PAYMENT.status]) !== 'confirmed') continue;
    const c = cents(Number(af[APPLICATION.amount]) || 0);
    collectedC += sel(p.fields?.[PAYMENT.entryType]) === 'refund' ? -c : c;
  }
  const pendingC = Math.max(amountC - collectedC, 0);
  const cancelled = sel(f[CHARGE.status]) === 'cancelled';
  const due = String(f[CHARGE.dueOn] || '');
  const state = cancelled ? 'cancelled' : pendingC === 0 && amountC > 0 ? 'paid' : collectedC > 0 ? 'partial' : due && due < today ? 'overdue' : 'requested';
  return { amount: money(amountC), currency: f[CHARGE.currency] || '', collected: money(collectedC), pending: cancelled ? 0 : money(pendingC), state, overdue: !cancelled && pendingC > 0 && !!due && due < today, dueOn: due };
}

/** Whether a charge has active applications (then its amount is not edited and it is not cancelled). */
export const hasActiveApplications = (charge, applications = []) => applications.some(a => sel(a.fields?.[APPLICATION.status]) === 'active' && links(a.fields?.[APPLICATION.charge]).includes(charge.id));

const logLine = (by, now, what) => `${now.toISOString().slice(0, 16).replace('T', ' ')} UTC · ${text(by, 200)}: ${what}`;

/** Editing concept, amount or due day: only an open charge without active applications; the change is logged. */
export function editChargePatch(charge, changes = {}, { applications = [], by = '', now = new Date() } = {}) {
  const f = charge?.fields || {};
  if (sel(f[CHARGE.status]) !== 'open') fail('charge_cancelled');
  if (hasActiveApplications(charge, applications)) fail('charge_has_applications');
  const out = {}; const parts = [];
  if (changes.concept !== undefined) {
    const c = text(changes.concept, 200); if (c.length < 2) fail('concept_required');
    if (c !== f[CHARGE.concept]) { out[CHARGE.concept] = c; parts.push(`concepto «${f[CHARGE.concept] || ''}» → «${c}»`); }
  }
  if (changes.amount !== undefined) {
    const n = Number(changes.amount);
    if (!Number.isFinite(n) || n <= 0 || Math.abs(n * 100 - Math.round(n * 100)) > 1e-6) fail('invalid_amount');
    if (cents(n) !== cents(f[CHARGE.amount] || 0)) { out[CHARGE.amount] = money(cents(n)); parts.push(`importe ${f[CHARGE.amount]} → ${money(cents(n))} ${f[CHARGE.currency] || ''}`); }
  }
  if (changes.dueOn !== undefined) {
    if (!DAY_RE.test(String(changes.dueOn)) || Number.isNaN(Date.parse(`${changes.dueOn}T12:00:00Z`))) fail('invalid_due');
    if (changes.dueOn !== f[CHARGE.dueOn]) { out[CHARGE.dueOn] = changes.dueOn; parts.push(`vencimiento ${f[CHARGE.dueOn] || '—'} → ${changes.dueOn}`); }
  }
  if (!parts.length) fail('no_change');
  out[CHARGE.log] = [logLine(by, now, `editado: ${parts.join('; ')}.`), f[CHARGE.log] || ''].filter(Boolean).join('\n');
  return out;
}

/** Cancelling: with a written reason and a confirmation, only without active applications. Never deleted. */
export function cancelChargePatch(charge, { reason, confirm }, { applications = [], by = '', now = new Date() } = {}) {
  const f = charge?.fields || {};
  if (sel(f[CHARGE.status]) === 'cancelled') fail('charge_cancelled');
  if (hasActiveApplications(charge, applications)) fail('charge_has_applications');
  const r = text(reason, 500);
  if (r.length < 10) fail('reason_required');
  if (confirm !== true) fail('confirm_required');
  return {
    [CHARGE.status]: 'cancelled', [CHARGE.cancelledAt]: now.toISOString(), [CHARGE.cancelReason]: r,
    [CHARGE.log]: [logLine(by, now, `cancelado. Motivo: ${r}`), f[CHARGE.log] || ''].filter(Boolean).join('\n'),
  };
}

/**
 * A charge of an earlier version that Ale decided keeps being owed alongside a later accepted version:
 * a line in its log, `[vigente-con:<quote id>]`, written only by her explicit decision (charges.mts ›
 * keepWithVersion). Never inferred.
 */
export const KEEP_MARK = '[vigente-con:';
/**
 * A charge of a quote version that is an independent additional contract (beyond the agreed balance):
 * marked explicitly by Ale with a reason, `[adicional]` in its log. Never inferred; never counted as part of
 * the version's plan, so it never hides a duplicated debt.
 */
export const ADDITIONAL_MARK = '[adicional]';
export const isAdditional = (f = {}) => String(f[CHARGE.log] || '').includes(ADDITIONAL_MARK);
export const keptWith = (f = {}, quoteId) => String(f[CHARGE.log] || '').includes(`${KEEP_MARK}${quoteId}]`);

/** The agreed plan, as it travels in a charge's log. */
export const encodePlan = (plan) => `plan=${JSON.stringify(plan.map(p => ({ kind: p.kind, amount: money(cents(p.amount)), dueOn: p.dueOn })))}`;
export function decodePlan(log = '') {
  const line = String(log).split('\n').find(l => l.startsWith('plan='));
  if (!line) return null;
  try { const v = JSON.parse(line.slice(5)); return Array.isArray(v) ? v : null; } catch { return null; }
}

/** Every charge of a quote version, cancelled too: once a version was handled with charges, its legacy payment status stays history. */
export const chargesOfQuote = (charges = [], quoteId) => charges.filter(c => links(c.fields?.[CHARGE.quote]).includes(quoteId));

/** The open charges of a quote version: then its payment is read only from charges (legacy fields stay as history). */
export const openChargesOfQuote = (charges = [], quoteId) => charges.filter(c => links(c.fields?.[CHARGE.quote]).includes(quoteId) && sel(c.fields?.[CHARGE.status]) === 'open');

/** Per currency: what the open charges of a quote version add up to, against the version's one-off total (shown, never forced). */
export function planVsQuote(charges = [], quote) {
  const f = quote?.fields || {};
  let lines = []; try { lines = JSON.parse(f['Quote Lines JSON'] || '[]'); } catch { lines = []; }
  const quoteC = (Array.isArray(lines) ? lines : []).reduce((s, l) => s + cents(Number(l?.onceFee) || 0), 0);
  const planC = openChargesOfQuote(charges, quote?.id).reduce((s, c) => s + cents(Number(c.fields?.[CHARGE.amount]) || 0), 0);
  return { currency: f['Quote Currency'] || 'USD', quote: money(quoteC), plan: money(planC), difference: money(planC - quoteC) };
}

// ── Planned method (how each installment is expected to be paid) ──
export const PLANNED_MARK = '[medio previsto: ';
/** The planned method of a charge: its field, or (only if the field were missing: fldq438XeBZhfP4L1) the line in its log. */
export function plannedMethodOf(f = {}) {
  if (f[CHARGE.plannedMethod]) return String(f[CHARGE.plannedMethod]);
  const m = String(f[CHARGE.log] || '').match(/\[medio previsto: ([^\]]{1,80})\]/);
  return m ? m[1] : '';
}
/** The same charge fields without the planned-method field: the method goes to the log (only if the field were missing). */
export function withoutPlannedMethodField(fields = {}) {
  const method = fields[CHARGE.plannedMethod];
  if (!method) return fields;
  const { [CHARGE.plannedMethod]: _, ...rest } = fields;
  return { ...rest, [CHARGE.log]: `${PLANNED_MARK}${text(method, 80)}]\n${rest[CHARGE.log] || ''}` };
}

// ── The rest of a partly paid installment (docs/servicios/cobros.md › «Registro simple») ──
// After a partial payment, Ale decides explicitly what happens with the rest: it is asked on another day (a new
// charge for exactly the rest) or added to the next installment of the same plan. The installment keeps what was
// paid (its amount becomes what it collected, never less); the plan's total never changes (no debt asked twice);
// issued receipts are never touched. Each write carries `[ajuste:<key>]`, so a retry never applies it twice.
export const ADJUST_MARK = '[ajuste:';
export const REST_OF_MARK = '[resto-de:';
const hasMark = (f, opKey) => String(f?.[CHARGE.log] || '').includes(`${ADJUST_MARK}${opKey}]`);
export const restOfCharge = (f = {}) => { const m = String(f[CHARGE.log] || '').match(/\[resto-de:(rec[A-Za-z0-9]{14})\]/); return m ? m[1] : ''; };
const payerKey = (f = {}) => `${sel(f[CHARGE.payerType])}:${links(f[sel(f[CHARGE.payerType]) === 'company' ? CHARGE.account : CHARGE.stakeholder])[0] || ''}`;

/**
 * The plan a charge belongs to, by explicit links only (never by name or concept): its quote version, else its Plan
 * Key, else the charge it is the rest of (`[resto-de:<id>]`, followed to the first one), else itself. A single
 * payment whose rest was moved to another day is one plan with its rest, also for records written before this rule.
 */
export function planGroupOf(charge, charges = []) {
  const f = charge?.fields || {};
  const quote = links(f[CHARGE.quote])[0];
  if (quote) return `q:${quote}`;
  if (f[CHARGE.planKey]) return `p:${f[CHARGE.planKey]}`;
  let root = charge; const seen = new Set();
  for (;;) {
    const of = restOfCharge(root?.fields || {});
    if (!of || seen.has(of)) break;
    seen.add(of);
    const parent = charges.find(c => c.id === of);
    if (!parent) { root = { id: of, fields: {} }; break; }
    const pf = parent.fields || {};
    if (links(pf[CHARGE.quote])[0]) return `q:${links(pf[CHARGE.quote])[0]}`;
    if (pf[CHARGE.planKey]) return `p:${pf[CHARGE.planKey]}`;
    root = parent;
  }
  return `c:${root.id}`;
}

/** The next open installment of the same plan (or, without a plan, of the same quote version), same payer and currency. */
export function nextInstallmentOf(charge, charges = []) {
  const f = charge?.fields || {};
  const plan = f[CHARGE.planKey];
  const quote = links(f[CHARGE.quote])[0];
  const after = (c) => String(c.fields?.[CHARGE.dueOn] || '') > String(f[CHARGE.dueOn] || '') || (String(c.fields?.[CHARGE.dueOn] || '') === String(f[CHARGE.dueOn] || '') && (Number(c.fields?.[CHARGE.installmentNumber]) || 0) > (Number(f[CHARGE.installmentNumber]) || 0));
  return charges
    .filter(c => c.id !== charge.id && sel(c.fields?.[CHARGE.status]) === 'open' && c.fields?.[CHARGE.currency] === f[CHARGE.currency] && payerKey(c.fields) === payerKey(f))
    .filter(c => planGroupOf(c, charges) === planGroupOf(charge, charges))
    .filter(after)
    .sort((a, b) => String(a.fields?.[CHARGE.dueOn] || '').localeCompare(String(b.fields?.[CHARGE.dueOn] || '')) || (Number(a.fields?.[CHARGE.installmentNumber]) || 0) - (Number(b.fields?.[CHARGE.installmentNumber]) || 0))[0] || null;
}

/**
 * What a partial payment does to the plan, before anything is written (the screen shows it; the server applies it).
 * input: { charge, charges, collected (before this payment), paidNow, mode: 'keep' | 'later' | 'next', dueOn, opKey, by, now }
 * Returns { rest, mode, patches: [{ id, fields }], create: [fields], next, preview } — with mode 'keep' nothing changes.
 */
export function restAdjustment({ charge, charges = [], collected = 0, paidNow, mode = 'keep', dueOn = '', opKey = '', by = '', now = new Date() }) {
  const f = charge?.fields || {};
  if (sel(f[CHARGE.status]) !== 'open') fail('charge_cancelled');
  const amountC = cents(f[CHARGE.amount] || 0);
  const pendingC = Math.max(amountC - cents(collected), 0);
  const paidC = cents(paidNow);
  if (!(paidC > 0) || paidC > pendingC) fail('invalid_amount');
  const restC = pendingC - paidC;
  const out = { rest: money(restC), mode, patches: [], create: [], next: null, preview: { installment: money(amountC), installmentAfter: money(amountC) } };
  if (!restC || mode === 'keep') return { ...out, mode: restC ? 'keep' : 'none' };
  if (!KEY_RE.test(String(opKey))) fail('key_required');
  const newAmountC = amountC - restC; // = collected + paid now: never below what it collected
  const stamp = logLine(by, now, '');
  const cur = f[CHARGE.currency] || '';
  if (mode === 'later') {
    if (!DAY_RE.test(String(dueOn)) || Number.isNaN(Date.parse(`${dueOn}T12:00:00Z`))) fail('invalid_due');
    const fields = {
      [CHARGE.name]: `${f[CHARGE.concept] || 'Cobro'} (resto) · ${String(f[CHARGE.name] || '').split(' · ').slice(-1)[0]}`,
      [CHARGE.origin]: f[CHARGE.origin], [CHARGE.payerType]: f[CHARGE.payerType], [CHARGE.concept]: f[CHARGE.concept],
      [CHARGE.amount]: money(restC), [CHARGE.currency]: cur, [CHARGE.dueOn]: dueOn, [CHARGE.installmentKind]: 'balance',
      [CHARGE.status]: 'open', [CHARGE.requestedAt]: now.toISOString(), [CHARGE.createdBy]: text(by, 200), [CHARGE.key]: `rest-${opKey}`.slice(0, 64),
      [CHARGE.log]: `${ADJUST_MARK}${opKey}] ${REST_OF_MARK}${charge.id}] ${stamp}resto de ${money(restC)} ${cur} de «${f[CHARGE.name] || ''}», a cobrar el ${dueOn}.`,
    };
    for (const k of [CHARGE.stakeholder, CHARGE.account, CHARGE.beneficiary, CHARGE.quote]) if (links(f[k]).length) fields[k] = links(f[k]);
    if (f[CHARGE.account]) fields[CHARGE.accountIsReference] = !!f[CHARGE.accountIsReference];
    if (f[CHARGE.planKey]) fields[CHARGE.planKey] = f[CHARGE.planKey];
    if (plannedMethodOf(f)) fields[CHARGE.plannedMethod] = plannedMethodOf(f);
    out.create.push(fields);
    out.preview = { installment: money(amountC), installmentAfter: money(newAmountC), restOn: dueOn };
  } else if (mode === 'next') {
    const next = nextInstallmentOf(charge, charges);
    if (!next) fail('no_next_installment');
    const nf = next.fields || {};
    const nextAfterC = cents(nf[CHARGE.amount] || 0) + restC;
    if (!hasMark(nf, opKey)) {
      out.patches.push({ id: next.id, fields: {
        [CHARGE.amount]: money(nextAfterC),
        [CHARGE.log]: [`${ADJUST_MARK}${opKey}] ${stamp}importe ${nf[CHARGE.amount]} → ${money(nextAfterC)} ${cur}: se suma el resto (${money(restC)}) de «${f[CHARGE.name] || ''}».`, nf[CHARGE.log] || ''].filter(Boolean).join('\n'),
      } });
    }
    out.next = { id: next.id, before: Number(nf[CHARGE.amount]) || 0, after: money(nextAfterC), dueOn: nf[CHARGE.dueOn] };
    out.preview = { installment: money(amountC), installmentAfter: money(newAmountC), next: out.next };
  } else fail('invalid_rest_mode');
  // The installment itself is written last: its mark says the whole adjustment was applied
  if (!hasMark(f, opKey)) {
    out.patches.push({ id: charge.id, fields: {
      [CHARGE.amount]: money(newAmountC),
      [CHARGE.log]: [`${ADJUST_MARK}${opKey}] ${stamp}importe ${f[CHARGE.amount]} → ${money(newAmountC)} ${cur} (lo cobrado); el resto (${money(restC)}) ${mode === 'later' ? `se cobra el ${dueOn}` : 'se suma a la cuota siguiente'}.`, f[CHARGE.log] || ''].filter(Boolean).join('\n'),
    } });
  }
  return out;
}
/** The adjustment `opKey` was already applied to this installment (its last write). */
export const adjustmentApplied = (charge, opKey) => hasMark(charge?.fields || {}, opKey);
